import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { interviewService } from '@/services/interview.service'
import { InterviewChecklist } from '@/components/InterviewChecklist'
import { InterviewChecklistItem, ApplicantStatus } from '@shared/types/entities'
import { InterviewResponse, SetAvailabilityRequest, InterviewerSummary } from '@shared/types/api'
import { AdminLayout } from '@/components/AdminLayout'

type Tab = 'upcoming' | 'completed' | 'availability'

const DAYS_OF_WEEK = [
  { key: '1', label: 'Mo' },
  { key: '2', label: 'Di' },
  { key: '3', label: 'Mi' },
  { key: '4', label: 'Do' },
  { key: '5', label: 'Fr' },
  { key: '6', label: 'Sa' },
  { key: '0', label: 'So' },
]

export function InterviewManagementPage() {
  const [activeTab, setActiveTab] = useState<Tab>('upcoming')
  const [reviewingInterview, setReviewingInterview] = useState<InterviewResponse | null>(null)
  const [selectedInterviewerId, setSelectedInterviewerId] = useState<string>('')
  const queryClient = useQueryClient()

  const [viewingInterview, setViewingInterview] = useState<InterviewResponse | null>(null)

  // Upcoming interviews
  const { data: upcomingData, isLoading: loadingInterviews } = useQuery({
    queryKey: ['upcoming-interviews'],
    queryFn: () => interviewService.getUpcomingInterviews(),
    enabled: activeTab === 'upcoming',
  })

  // Completed interviews
  const { data: completedData, isLoading: loadingCompleted } = useQuery({
    queryKey: ['completed-interviews'],
    queryFn: () => interviewService.getCompletedInterviews(),
    enabled: activeTab === 'completed',
  })

  // List of interviewers
  const { data: interviewersData, isLoading: loadingInterviewers } = useQuery({
    queryKey: ['interviewers'],
    queryFn: () => interviewService.listInterviewers(),
    enabled: activeTab === 'availability',
  })

  // Availability for selected interviewer
  const { data: availabilityData, isLoading: loadingAvailability } = useQuery({
    queryKey: ['interviewer-availability', selectedInterviewerId],
    queryFn: () => interviewService.getAvailability(selectedInterviewerId),
    enabled: activeTab === 'availability' && !!selectedInterviewerId,
  })

  // Submit review mutation
  const reviewMutation = useMutation({
    mutationFn: (params: { id: string; checklist: InterviewChecklistItem[]; result: ApplicantStatus.APPROVED | ApplicantStatus.REJECTED | ApplicantStatus.REVISION_REQUIRED; notes?: string }) =>
      interviewService.submitReview(params.id, {
        checklist: params.checklist,
        result: params.result,
        notes: params.notes,
      }),
    onSuccess: () => {
      setReviewingInterview(null)
      queryClient.invalidateQueries({ queryKey: ['upcoming-interviews'] })
      queryClient.invalidateQueries({ queryKey: ['completed-interviews'] })
    },
  })

  // Save availability mutation
  const [availabilityForm, setAvailabilityForm] = useState<{
    googleEmail: string
    displayName: string
    selectedDays: Record<string, boolean>
    startTime: string
    endTime: string
    isActive: boolean
  }>({
    googleEmail: '',
    displayName: '',
    selectedDays: { '1': true, '2': true, '3': true, '4': true, '5': true },
    startTime: '09:00',
    endTime: '12:00',
    isActive: true,
  })

  const availabilityMutation = useMutation({
    mutationFn: (data: SetAvailabilityRequest) => interviewService.setAvailability(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interviewer-availability', selectedInterviewerId] })
      queryClient.invalidateQueries({ queryKey: ['interviewers'] })
    },
  })

  const handleSaveAvailability = () => {
    const weeklyPattern: Record<string, Array<{ startTime: string; endTime: string; slotDurationMinutes: number }>> = {}
    for (const [day, active] of Object.entries(availabilityForm.selectedDays)) {
      if (active) {
        weeklyPattern[day] = [{
          startTime: availabilityForm.startTime,
          endTime: availabilityForm.endTime,
          slotDurationMinutes: 15,
        }]
      }
    }
    availabilityMutation.mutate({
      interviewerId: selectedInterviewerId,
      googleEmail: availabilityForm.googleEmail,
      displayName: availabilityForm.displayName,
      weeklyPattern,
      isActive: availabilityForm.isActive,
    })
  }

  // Auto-select first interviewer when list loads
  useEffect(() => {
    if (interviewersData?.interviewers?.length && !selectedInterviewerId) {
      setSelectedInterviewerId(interviewersData.interviewers[0].interviewerId)
    }
  }, [interviewersData, selectedInterviewerId])

  // Populate form when availability data loads
  useEffect(() => {
    const existing = availabilityData?.availability
    if (existing) {
      const firstDay = Object.entries(existing.weeklyPattern).find(([, slots]) => slots.length > 0)
      setAvailabilityForm({
        googleEmail: existing.googleEmail,
        displayName: existing.displayName,
        selectedDays: Object.fromEntries(
          DAYS_OF_WEEK.map(d => [d.key, (existing.weeklyPattern[d.key]?.length || 0) > 0])
        ),
        startTime: firstDay?.[1]?.[0]?.startTime || '09:00',
        endTime: firstDay?.[1]?.[0]?.endTime || '12:00',
        isActive: existing.isActive,
      })
    } else if (availabilityData && !availabilityData.availability) {
      setAvailabilityForm({
        googleEmail: '',
        displayName: '',
        selectedDays: { '1': true, '2': true, '3': true, '4': true, '5': true },
        startTime: '09:00',
        endTime: '12:00',
        isActive: true,
      })
    }
  }, [availabilityData])

  return (
    <AdminLayout>
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Interview-Verwaltung</h1>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`pb-2 px-1 text-sm font-medium border-b-2 ${
              activeTab === 'upcoming' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Anstehende Interviews
          </button>
          <button
            onClick={() => setActiveTab('completed')}
            className={`pb-2 px-1 text-sm font-medium border-b-2 ${
              activeTab === 'completed' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Abgeschlossen
          </button>
          <button
            onClick={() => setActiveTab('availability')}
            className={`pb-2 px-1 text-sm font-medium border-b-2 ${
              activeTab === 'availability' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Verfügbarkeit
          </button>
        </nav>
      </div>

      {/* Upcoming Interviews Tab */}
      {activeTab === 'upcoming' && (
        <div>
          {loadingInterviews ? (
            <p className="text-gray-600">Lade Interviews...</p>
          ) : (upcomingData?.interviews?.length || 0) === 0 ? (
            <p className="text-gray-500">Keine anstehenden Interviews.</p>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Datum</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bewerber</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Meet-Link</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aktion</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {upcomingData?.interviews?.map(interview => (
                    <tr key={interview.id}>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {new Date(interview.scheduledAt).toLocaleString('de-DE', {
                          weekday: 'short',
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {interview.applicantName}
                        {interview.applicantEmail && (
                          <span className="text-xs text-gray-500 block">{interview.applicantEmail}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <a
                          href={interview.meetLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          Meeting beitreten
                        </a>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <button
                          onClick={() => setReviewingInterview(interview)}
                          className="px-3 py-1 bg-indigo-600 text-white rounded text-xs font-medium hover:bg-indigo-700"
                        >
                          Bewerten
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Completed Interviews Tab */}
      {activeTab === 'completed' && (
        <div>
          {loadingCompleted ? (
            <p className="text-gray-600">Lade abgeschlossene Interviews...</p>
          ) : (completedData?.interviews?.length || 0) === 0 ? (
            <p className="text-gray-500">Keine abgeschlossenen Interviews.</p>
          ) : (
            <div className="space-y-4">
              {completedData?.interviews?.map(interview => (
                <div key={interview.id} className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className="font-medium text-gray-900">{interview.applicantName}</span>
                      {interview.applicantEmail && (
                        <span className="text-sm text-gray-500 ml-2">{interview.applicantEmail}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        interview.result === ApplicantStatus.APPROVED
                          ? 'bg-green-100 text-green-800'
                          : interview.result === ApplicantStatus.REJECTED
                          ? 'bg-red-100 text-red-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {interview.result === ApplicantStatus.APPROVED ? 'Genehmigt' :
                         interview.result === ApplicantStatus.REJECTED ? 'Abgelehnt' : 'Überarbeitung'}
                      </span>
                      <button
                        onClick={() => setViewingInterview(viewingInterview?.id === interview.id ? null : interview)}
                        className="text-sm text-blue-600 hover:underline"
                      >
                        {viewingInterview?.id === interview.id ? 'Schließen' : 'Details'}
                      </button>
                    </div>
                  </div>
                  <div className="text-sm text-gray-500">
                    <span>Interview: {new Date(interview.scheduledAt).toLocaleString('de-DE', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                    <span className="mx-2">·</span>
                    <span>Interviewer: {interview.interviewerName}</span>
                    {interview.reviewedAt && (
                      <>
                        <span className="mx-2">·</span>
                        <span>Bewertet: {new Date(interview.reviewedAt).toLocaleString('de-DE', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                      </>
                    )}
                  </div>

                  {viewingInterview?.id === interview.id && (
                    <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                      {interview.reviewNotes && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-700">Notizen</h4>
                          <p className="text-sm text-gray-600 mt-1">{interview.reviewNotes}</p>
                        </div>
                      )}
                      {interview.checklist?.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Checkliste</h4>
                          <div className="grid gap-2">
                            {interview.checklist.map(item => (
                              <div key={item.id} className="flex items-center justify-between text-sm bg-gray-50 rounded px-3 py-2">
                                <span className="text-gray-700">{item.label}</span>
                                <span className={`font-medium ${
                                  item.rating === 'BESTANDEN' ? 'text-green-600' :
                                  item.rating === 'TEILWEISE' ? 'text-yellow-600' :
                                  item.rating === 'NICHT_BESTANDEN' ? 'text-red-600' : 'text-gray-400'
                                }`}>
                                  {item.rating === 'BESTANDEN' ? '✓ Bestanden' :
                                   item.rating === 'TEILWEISE' ? '~ Teilweise' :
                                   item.rating === 'NICHT_BESTANDEN' ? '✗ Nicht bestanden' : '—'}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Availability Tab */}
      {activeTab === 'availability' && (
        <div className="space-y-4">
          {/* Interviewer selector */}
          <div className="bg-white rounded-lg shadow p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Interviewer</label>
            {loadingInterviewers ? (
              <p className="text-gray-500 text-sm">Lade Interviewer...</p>
            ) : (
              <div className="flex gap-2 flex-wrap">
                {interviewersData?.interviewers?.map((interviewer: InterviewerSummary) => (
                  <button
                    key={interviewer.interviewerId}
                    onClick={() => setSelectedInterviewerId(interviewer.interviewerId)}
                    className={`px-4 py-2 rounded-md text-sm font-medium ${
                      selectedInterviewerId === interviewer.interviewerId
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {interviewer.displayName}
                    {!interviewer.isActive && <span className="ml-1 text-xs opacity-70">(inaktiv)</span>}
                  </button>
                ))}
                {(!interviewersData?.interviewers?.length) && (
                  <p className="text-gray-500 text-sm">Noch keine Interviewer angelegt. Bitte unten einen neuen anlegen.</p>
                )}
              </div>
            )}
          </div>

          {/* Availability form */}
          {(selectedInterviewerId || !interviewersData?.interviewers?.length) && (
            <div className="bg-white rounded-lg shadow p-6">
              {loadingAvailability && selectedInterviewerId ? (
                <p className="text-gray-600">Lade Verfügbarkeit...</p>
              ) : (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Google Email</label>
                      <input
                        type="email"
                        value={availabilityForm.googleEmail}
                        onChange={e => setAvailabilityForm(f => ({ ...f, googleEmail: e.target.value }))}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        placeholder="name@u-like-it.de"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Anzeigename</label>
                      <input
                        type="text"
                        value={availabilityForm.displayName}
                        onChange={e => setAvailabilityForm(f => ({ ...f, displayName: e.target.value }))}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        placeholder="Max Mustermann"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Wochentage</label>
                    <div className="flex gap-2">
                      {DAYS_OF_WEEK.map(day => (
                        <button
                          key={day.key}
                          onClick={() => setAvailabilityForm(f => ({
                            ...f,
                            selectedDays: { ...f.selectedDays, [day.key]: !f.selectedDays[day.key] },
                          }))}
                          className={`w-10 h-10 rounded-full text-sm font-medium ${
                            availabilityForm.selectedDays[day.key]
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {day.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Von</label>
                      <input
                        type="time"
                        value={availabilityForm.startTime}
                        onChange={e => setAvailabilityForm(f => ({ ...f, startTime: e.target.value }))}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Bis</label>
                      <input
                        type="time"
                        value={availabilityForm.endTime}
                        onChange={e => setAvailabilityForm(f => ({ ...f, endTime: e.target.value }))}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="isActive"
                      checked={availabilityForm.isActive}
                      onChange={e => setAvailabilityForm(f => ({ ...f, isActive: e.target.checked }))}
                      className="rounded border-gray-300"
                    />
                    <label htmlFor="isActive" className="text-sm text-gray-700">
                      Verfügbar für Interviews
                    </label>
                  </div>

                  <button
                    onClick={handleSaveAvailability}
                    disabled={availabilityMutation.isPending || !availabilityForm.googleEmail || !availabilityForm.displayName}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                  >
                    {availabilityMutation.isPending ? 'Speichern...' : 'Verfügbarkeit speichern'}
                  </button>

                  {availabilityMutation.isSuccess && (
                    <p className="text-sm text-green-600">Verfügbarkeit gespeichert</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Interview Checklist Modal */}
      {reviewingInterview && (
        <InterviewChecklist
          onSubmit={(checklist, result, notes) => {
            reviewMutation.mutate({
              id: reviewingInterview.id,
              checklist,
              result,
              notes,
            })
          }}
          onCancel={() => setReviewingInterview(null)}
          isSubmitting={reviewMutation.isPending}
        />
      )}
    </div>
    </AdminLayout>
  )
}
